/* eslint-disable complexity */
/* eslint-disable max-statements */
/* eslint-disable max-lines */
import { useCallback, useContext, useMemo } from 'react';
import { ControlledDateTimePickerContext } from '../../../ControlledDateTimePickerCTX.js';
import type { DSControlledDateTimePickerT } from '../../../react-desc-prop-types.js';
import type { DSControlledDateTimePickerInternalsT } from '../../../sharedTypes.js';
import {
  compareTwoDateAreInSameMonth,
  isSecondDateInNextMonth,
  isSecondDateInPreviousMonth,
  sumValuesToDate,
} from '../../../utils/dateHelpers.js';
import type { useCurrentDisplayedMonthYearLogic } from './useCurrentDisplayedMonthYearLogic.js';
import type { useFocusLogic } from './useFocusLogic.js';

interface UseKeyboardHandlersArgsT {
  currentDisplayedMonthLogic: ReturnType<typeof useCurrentDisplayedMonthYearLogic>;
  focusLogic: ReturnType<typeof useFocusLogic>;
}
type HandleDayOnKeyDownT = (e: React.KeyboardEvent, day: DSControlledDateTimePickerT.MetaMonthDay) => void;
interface KeyboardHandlersT {
  handleDayOnKeyDown: HandleDayOnKeyDownT;
  handlePrevYearKeyDown: (e: React.KeyboardEvent) => void;
  handlePrevMonthKeyDown: (e: React.KeyboardEvent) => void;
  handleNextMonthKeyDown: (e: React.KeyboardEvent) => void;
  handleNextYearKeyDown: (e: React.KeyboardEvent) => void;
  handlePickerIconKeyDown: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
}
export const useKeyboardHandlers = ({
  currentDisplayedMonthLogic,
  focusLogic,
}: UseKeyboardHandlersArgsT): KeyboardHandlersT => {
  const {
    isControllerOnly,
    isWithTimeWheelToo,
    withAnyInputs,
    isWithTimeInputs,
    isWithDateInputs,
    trackFocusFirstSegment,
    trackFocusMeridiemInput,
    trackFocusYearInput,
    trackFocusCalendarPrevMonth,
    trackFocusCalendarNextMonth,
    trackFocusCalendarNextYear,
    trackFocusCalendarPrevYear,
    trackFocusTimewheelCurrMeridiem,
    trackFocusCalendarMetafocusedDay,
    trackFocusTimewheelCurrHour,
  } = useContext(ControlledDateTimePickerContext);
  const {
    focusedDay,
    handleFocusMetaDay,
    handleFocusMetaDayByDay,
    //  tryToFocusCurrentlyDataIsFocusedDay
  } = focusLogic;
  const { daysArray, handlePrevMonth, handleNextMonth } = currentDisplayedMonthLogic;

  const tryToFocusDayRegion = useCallback(() => {
    // If there's a focusedDay, check if it's still visible
    const isFocusedStillVisible = focusedDay
      ? daysArray.some(({ day }) => compareTwoDateAreInSameMonth(day, focusedDay.day))
      : false;

    // If no focusedDay or the existing one went off-screen, pick the first current-month day
    if (!focusedDay || !isFocusedStillVisible) {
      const firstCurrMonth = daysArray.find((d) => d.isCurrMonthDay);
      if (firstCurrMonth) {
        handleFocusMetaDay(firstCurrMonth);
      }
    }

    trackFocusCalendarMetafocusedDay();
  }, [daysArray, focusedDay, handleFocusMetaDay, trackFocusCalendarMetafocusedDay]);

  const onHomeKeyDownInsideCalendar = useCallback(() => {
    trackFocusCalendarPrevMonth();
  }, [trackFocusCalendarPrevMonth]);
  const onEndKeyDownInsideCalendar = useCallback(() => {
    if (isWithTimeWheelToo) trackFocusTimewheelCurrMeridiem();
    else tryToFocusDayRegion();
  }, [isWithTimeWheelToo, trackFocusTimewheelCurrMeridiem, tryToFocusDayRegion]);

  const onHomeKeyDownTriggerIcon = useCallback(() => {
    trackFocusFirstSegment();
  }, [trackFocusFirstSegment]);
  const handlePickerIconKeyDown: DSControlledDateTimePickerInternalsT.OnKeyDown<HTMLButtonElement> = useCallback(
    (e) => {
      const { key } = e;
      if (['Home', 'End'].includes(key)) e.preventDefault();
      if (['Home', 'End'].includes(key)) e.stopPropagation();
      if (key === 'Home') onHomeKeyDownTriggerIcon();
      if (key === 'Backspace' && withAnyInputs) {
        if (isWithTimeInputs) trackFocusMeridiemInput();
        else if (isWithDateInputs) trackFocusYearInput();
      }
    },
    [
      isWithDateInputs,
      isWithTimeInputs,
      onHomeKeyDownTriggerIcon,
      trackFocusMeridiemInput,
      trackFocusYearInput,
      withAnyInputs,
    ],
  );
  // pending ux deciding what's going to happen, should we prevent changing month?
  //  as a strict "picker" interpretation we should prevent it if disabled/readonly/ariaDisabled
  //  as a more general "calendar" interpretation we can choose to allow it.
  const shouldPreventMonthChange = false;

  const handleDayOnKeyDown = useCallback(
    (e: React.KeyboardEvent, metaDay: DSControlledDateTimePickerT.MetaMonthDay) => {
      let newFocusedDayDate: Date | undefined;
      const { day } = metaDay;
      const { key, shiftKey } = e;
      if (['Home', 'End', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) e.preventDefault();
      if (['Home', 'End', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) e.stopPropagation();

      if (key === 'ArrowUp') newFocusedDayDate = sumValuesToDate(day, { daysToSum: -7 });
      if (key === 'ArrowDown') newFocusedDayDate = sumValuesToDate(day, { daysToSum: 7 });
      if (key === 'ArrowLeft') newFocusedDayDate = sumValuesToDate(day, { daysToSum: -1 });
      if (key === 'ArrowRight') newFocusedDayDate = sumValuesToDate(day, { daysToSum: 1 });

      if (newFocusedDayDate) {
        const newDayShareTheSameMonth = compareTwoDateAreInSameMonth(day, newFocusedDayDate);
        if (!newDayShareTheSameMonth && shouldPreventMonthChange) return;
        if (isSecondDateInPreviousMonth(day, newFocusedDayDate)) handlePrevMonth();
        if (isSecondDateInNextMonth(day, newFocusedDayDate)) handleNextMonth();
        handleFocusMetaDayByDay(newFocusedDayDate);
      }
      if (key === 'Home') onHomeKeyDownInsideCalendar();
      if (key === 'End') onEndKeyDownInsideCalendar();
      // TAB CYCLE IMPLEMENTATION
      if (key === 'Tab' && !shiftKey && isControllerOnly === false && !isWithTimeWheelToo) {
        // MUST prevent default since this is a custom focus switch
        // NOT preventing the default event will cause unexpected result
        e.preventDefault();
        trackFocusCalendarPrevMonth();
      }
      if (key === 'Tab' && !shiftKey && isControllerOnly === false && isWithTimeWheelToo) {
        // MUST prevent default since this is a custom focus switch
        // NOT preventing the default event will cause unexpected result
        e.preventDefault();
        trackFocusTimewheelCurrHour();
      }
    },
    [
      handleFocusMetaDayByDay,
      handleNextMonth,
      handlePrevMonth,
      isControllerOnly,
      isWithTimeWheelToo,
      onEndKeyDownInsideCalendar,
      onHomeKeyDownInsideCalendar,
      shouldPreventMonthChange,
      trackFocusCalendarPrevMonth,
      trackFocusTimewheelCurrHour,
    ],
  );
  const handlePrevYearKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const { key, shiftKey } = e;
      if (key === 'Tab') {
        e.preventDefault();
        if (shiftKey) {
          trackFocusCalendarNextMonth();
        } else {
          trackFocusCalendarNextYear();
        }
      }
      if (key === 'Home') {
        onHomeKeyDownInsideCalendar();
        e.preventDefault();
      }
      if (key === 'End') {
        onEndKeyDownInsideCalendar();
        e.preventDefault();
      }
    },
    [onEndKeyDownInsideCalendar, onHomeKeyDownInsideCalendar, trackFocusCalendarNextMonth, trackFocusCalendarNextYear],
  );
  const handlePrevMonthKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const { key, shiftKey } = e;
      if (key === 'Tab') {
        if (shiftKey) {
          // TAB CYCLE IMPLEMENTATION
          if (!isControllerOnly) {
            e.preventDefault();
            if (isWithTimeWheelToo) trackFocusTimewheelCurrMeridiem();
            else tryToFocusDayRegion();
          }
        } else {
          e.preventDefault();
          trackFocusCalendarNextMonth();
        }
      }
      if (key === 'Home') {
        onHomeKeyDownInsideCalendar();
        e.preventDefault();
      }
      if (key === 'End') {
        onEndKeyDownInsideCalendar();
        e.preventDefault();
      }
    },
    [
      isControllerOnly,
      isWithTimeWheelToo,
      onEndKeyDownInsideCalendar,
      onHomeKeyDownInsideCalendar,
      trackFocusCalendarNextMonth,
      trackFocusTimewheelCurrMeridiem,
      tryToFocusDayRegion,
    ],
  );
  const handleNextMonthKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const { key, shiftKey } = e;
      if (key === 'Tab') {
        e.preventDefault();
        if (shiftKey) {
          trackFocusCalendarPrevMonth();
        } else {
          trackFocusCalendarPrevYear();
        }
      }
      if (key === 'Home') {
        onHomeKeyDownInsideCalendar();
        e.preventDefault();
      }
      if (key === 'End') {
        onEndKeyDownInsideCalendar();
        e.preventDefault();
      }
    },
    [onEndKeyDownInsideCalendar, onHomeKeyDownInsideCalendar, trackFocusCalendarPrevMonth, trackFocusCalendarPrevYear],
  );
  const handleNextYearKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const { key, shiftKey } = e;
      if (key === 'Tab') {
        e.preventDefault();
        if (shiftKey) {
          trackFocusCalendarPrevYear();
        }
        if (!shiftKey) tryToFocusDayRegion();
      }
      if (key === 'Home') {
        onHomeKeyDownInsideCalendar();
        e.preventDefault();
      }
      if (key === 'End') {
        onEndKeyDownInsideCalendar();
        e.preventDefault();
      }
    },
    [tryToFocusDayRegion, trackFocusCalendarPrevYear, onHomeKeyDownInsideCalendar, onEndKeyDownInsideCalendar],
  );

  return useMemo(
    () => ({
      handleDayOnKeyDown,
      handlePrevYearKeyDown,
      handlePrevMonthKeyDown,
      handleNextMonthKeyDown,
      handleNextYearKeyDown,
      handlePickerIconKeyDown,
    }),
    [
      handleDayOnKeyDown,
      handlePrevYearKeyDown,
      handlePrevMonthKeyDown,
      handleNextMonthKeyDown,
      handleNextYearKeyDown,
      handlePickerIconKeyDown,
    ],
  );
};
